import React, { useEffect, useRef, useState } from "react";
import { MdDeleteOutline } from "react-icons/md";
import { MdOutlineEdit } from "react-icons/md";
import { ToastContainer } from "react-toastify";
import { BiImageAdd } from "react-icons/bi";
import { notifyError, notifySuccess } from "../../hook/notification";
import FontService from "../../services/font.service";
import DeleteModel from "../../components/commonTable/deleteModel";

export default function Fonts() {
  const [fontGroups, setFontGroups] = useState([]);
  const [categories, setCategories] = useState([]);
  const [fontName, setFontName] = useState("");
  const [category, setCategory] = useState("");
  const [fontFile, setFontFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [adding, setAdding] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const fileRef = useRef();

  // Edit state
  const [editFont, setEditFont] = useState(null); // { id, fontName, category }
  const [editName, setEditName] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editFile, setEditFile] = useState(null);
  const [editFileName, setEditFileName] = useState("");
  const [saving, setSaving] = useState(false);
  const editFileRef = useRef();

  const fetchFonts = async () => {
    try {
      const res = await FontService.getAllFonts();
      setFontGroups(res?.data?.apiresponse?.data || []);
    } catch {
      notifyError("Failed to load fonts");
    }
  };

  useEffect(() => {
    FontService.getCategories().then((res) => {
      const cats = res?.data?.apiresponse?.data || [];
      setCategories(cats);
      if (cats.length > 0) {
        setCategory(cats[0].key);
        setEditCategory(cats[0].key);
      }
    }).catch(() => {});
    fetchFonts();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFontFile(file);
    setFileName(file.name);
  };

  const handleAdd = async () => {
    if (!fontName.trim()) return notifyError("Font name is required");
    if (!fontFile) return notifyError("Font file is required");

    const formData = new FormData();
    formData.append("fontName", fontName.trim());
    formData.append("category", category);
    formData.append("fontFile", fontFile);

    setAdding(true);
    try {
      await FontService.addFont(formData);
      notifySuccess("Font added successfully");
      setFontName("");
      setFontFile(null);
      setFileName("");
      setCategory(categories[0]?.key || "");
      if (fileRef.current) fileRef.current.value = "";
      await fetchFonts();
    } catch {
      notifyError("Failed to add font");
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    setDeletingId(confirmId);
    try {
      await FontService.deleteFont(confirmId);
      notifySuccess("Font deleted");
      setConfirmId(null);
      await fetchFonts();
    } catch {
      notifyError("Failed to delete font");
    } finally {
      setDeletingId(null);
    }
  };

  const openEdit = (font) => {
    setEditFont(font);
    setEditName(font.fontName);
    // group.category may be a displayName — resolve to key
    const matched = categories.find(
      (c) => c.key === font.category || c.displayName === font.category
    );
    setEditCategory(matched?.key || categories[0]?.key || "");
    setEditFile(null);
    setEditFileName("");
    if (editFileRef.current) editFileRef.current.value = "";
  };

  const closeEdit = () => {
    setEditFont(null);
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEditFile(file);
    setEditFileName(file.name);
  };

  const handleSaveEdit = async () => {
    if (!editName.trim()) return notifyError("Font name is required");

    const formData = new FormData();
    formData.append("fontName", editName.trim());
    formData.append("category", editCategory);
    if (editFile) formData.append("fontFile", editFile);

    setSaving(true);
    try {
      await FontService.updateFont(editFont.id, formData);
      notifySuccess("Font updated successfully");
      closeEdit();
      await fetchFonts();
    } catch {
      notifyError("Failed to update font");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 min-h-screen bg-gray-900 text-white">
      <ToastContainer />

      {/* ── Delete confirmation ── */}
      {confirmId && (
        <DeleteModel
          loader={deletingId === confirmId}
          unSetLoader={() => setConfirmId(null)}
          DeleteCategory={handleDelete}
        />
      )}

      {/* ── Edit modal ── */}
      {editFont && (
        <div className="fixed inset-0 bg-black/50 z-[999] flex items-center justify-center px-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md flex flex-col gap-4">
            <h2 className="text-lg font-semibold">Edit Font</h2>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-400">Font Name</label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="bg-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-400">Category</label>
              <select
                value={editCategory}
                onChange={(e) => setEditCategory(e.target.value)}
                className="bg-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500"
              >
                {categories.map((c) => (
                  <option key={c.key} value={c.key}>{c.displayName}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-400">New Font File (optional)</label>
              <label className="flex items-center gap-2 bg-gray-700 rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-600 transition">
                <BiImageAdd className="text-lg text-gray-300" />
                <span className="text-sm text-gray-300 truncate">
                  {editFileName || "Choose file to replace…"}
                </span>
                <input
                  ref={editFileRef}
                  type="file"
                  accept=".ttf,.otf,.woff,.woff2"
                  onChange={handleEditFileChange}
                  className="hidden"
                />
              </label>
            </div>

            <div className="flex gap-3 justify-end mt-2">
              <button
                type="button"
                onClick={closeEdit}
                className="px-5 py-2 rounded-lg border border-gray-600 text-gray-300 text-sm hover:bg-gray-700 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleSaveEdit}
                className="btn save"
              >
                {adding ? "Saving…" : "Save"}
              </button>

            </div>
          </div>
        </div>
      )}

      <h1 className="text-2xl font-bold mb-6">Fonts</h1>

      {/* ── Add Font Form ── */}
      <div className="bg-gray-800 rounded-xl p-6 mb-8 flex flex-col gap-4">
        <h2 className="text-lg font-semibold">Add New Font</h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400">Font Name</label>
            <input
              type="text"
              value={fontName}
              onChange={(e) => setFontName(e.target.value)}
              placeholder="e.g. Calcio"
              className="bg-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="bg-gray-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-blue-500"
            >
              {categories.map((c) => (
                <option key={c.key} value={c.key}>{c.displayName}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-400">Font File</label>
            <label className="flex items-center gap-2 bg-gray-700 rounded-lg px-3 py-2 cursor-pointer hover:bg-gray-600 transition">
              <BiImageAdd className="text-lg text-gray-300" />
              <span className="text-sm text-gray-300 truncate">
                {fileName || "Choose file…"}
              </span>
              <input
                ref={fileRef}
                type="file"
                accept=".ttf,.otf,.woff,.woff2"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            disabled={adding}
            onClick={handleAdd}
            className="btn save"
          >
            {adding ? "Adding…" : "Add Font"}
          </button>
        </div>
      </div>

      {/* ── Font List ── */}
      {fontGroups.length === 0 ? (
        <p className="text-gray-500 text-sm">No fonts added yet.</p>
      ) : (
        <div className="flex flex-col gap-6">
          {fontGroups.map((group) => (
            <div key={group.category} className="bg-gray-800 rounded-xl p-5">
              <h3 className="text-base font-semibold mb-3 text-blue-400">
                {group.category}
              </h3>
              <div className="flex flex-col gap-2">
                {group.fonts.map((font) => (
                  <div
                    key={font.id}
                    className="flex items-center justify-between bg-gray-700 rounded-lg px-4 py-3"
                  >
                    <span className="text-sm font-medium">{font.fontName}</span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEdit({ ...font, category: group.category })}
                        className="p-2"
                      >
                        <MdOutlineEdit className="text-xl text-sky-600" />
                      </button>
                      <button
                        onClick={() => setConfirmId(font.id)}
                        className="p-2"
                      >
                        <MdDeleteOutline className="text-xl text-red" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
